import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, X, Send, Sparkles } from "lucide-react";
import { sendChat } from "@/lib/api";

const SUGGESTIONS = [
    "Welche Stage ist für mein Auto sinnvoll?",
    "Verliere ich die Garantie?",
    "Ist DSG Optimierung notwendig?",
    "Welche Risiken gibt es?",
];

const sessionId = `chat-${(typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) || Date.now()}`;

export const AIChatWidget = () => {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([
        {
            role: "assistant",
            text: "Hallo! Ich bin dein KI Performance Advisor. Frag mich alles rund um Chiptuning, Stages, DSG-Optimierung oder Garantie. 🏁",
        },
    ]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, loading, open]);

    const send = async (text) => {
        const message = (text ?? input).trim();
        if (!message || loading) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", text: message }]);
        setLoading(true);
        try {
            const res = await sendChat({ session_id: sessionId, message });
            setMessages((m) => [...m, { role: "assistant", text: res.reply }]);
        } catch (e) {
            setMessages((m) => [
                ...m,
                { role: "assistant", text: "Entschuldige, da ist etwas schiefgelaufen. Bitte versuche es erneut." },
            ]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <button
                data-testid="ai-chat-toggle"
                onClick={() => setOpen((o) => !o)}
                aria-label="KI-Berater öffnen"
                className="fixed bottom-6 left-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-white shadow-lg shadow-black/40 hover:bg-[#FF3B30] transition-colors"
            >
                {open ? <X className="h-6 w-6" /> : <Bot className="h-7 w-7" />}
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: 24, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 24, scale: 0.96 }}
                        transition={{ duration: 0.2 }}
                        data-testid="ai-chat-panel"
                        className="fixed bottom-24 left-5 z-40 flex h-[70vh] max-h-[560px] w-[calc(100vw-2.5rem)] max-w-sm flex-col overflow-hidden rounded-2xl border border-border bg-[#0F0F0F] shadow-2xl"
                    >
                        <div className="flex items-center gap-3 border-b border-border bg-surface px-4 py-3.5">
                            <div className="relative flex h-9 w-9 items-center justify-center rounded-full bg-primary">
                                <Bot className="h-5 w-5 text-white" />
                                <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-surface bg-success" />
                            </div>
                            <div>
                                <div className="text-sm font-semibold text-white">KI Performance Advisor</div>
                                <div className="text-xs text-success">24/7 online</div>
                            </div>
                        </div>

                        <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
                            {messages.map((m, i) => (
                                <div
                                    key={i}
                                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                                >
                                    <div
                                        className={`max-w-[85%] whitespace-pre-line rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                                            m.role === "user"
                                                ? "rounded-br-sm bg-primary text-white"
                                                : "rounded-bl-sm bg-surface-elevated text-[#E4E4E7]"
                                        }`}
                                    >
                                        {m.text}
                                    </div>
                                </div>
                            ))}
                            {loading && (
                                <div className="flex justify-start">
                                    <div className="flex items-center gap-1.5 rounded-2xl rounded-bl-sm bg-surface-elevated px-4 py-3">
                                        {[0, 1, 2].map((d) => (
                                            <span
                                                key={d}
                                                className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#A1A1AA]"
                                                style={{ animationDelay: `${d * 0.15}s` }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {messages.length <= 1 && (
                                <div className="space-y-2 pt-2">
                                    {SUGGESTIONS.map((s) => (
                                        <button
                                            key={s}
                                            onClick={() => send(s)}
                                            className="flex w-full items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2 text-left text-xs text-[#A1A1AA] hover:border-primary/50 hover:text-white transition-colors"
                                        >
                                            <Sparkles className="h-3.5 w-3.5 text-primary" /> {s}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="border-t border-border bg-surface p-3">
                            <div className="flex items-center gap-2">
                                <input
                                    data-testid="ai-chat-input"
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && send()}
                                    placeholder="Frage stellen..."
                                    className="flex-1 rounded-lg border border-border bg-[#0A0A0A] px-3 py-2.5 text-sm text-white placeholder:text-[#52525B] focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                                />
                                <button
                                    data-testid="ai-chat-send"
                                    onClick={() => send()}
                                    disabled={loading}
                                    className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-white hover:bg-[#FF3B30] transition-colors disabled:opacity-50"
                                >
                                    <Send className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default AIChatWidget;
